import { useState, useEffect } from "react";

//styles
import "./Pickup.css";

//hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import { useUser } from "../../hooks/useUser";
import { usePreviousOrder } from "../../hooks/usePreviousOrder";
import reportError from "../../utils/reportError";

//components
import Loading from "../../components/Loading";

// Helper function to format date from MM/DD/YYYY to "Fri, Apr 24"
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { weekday: "short", month: "short", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

// Helper function to format date to "June 6"
const formatDateMonthDay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options);
};

// Helper function to calculate days remaining
const calculateDaysRemaining = (dateString) => {
  if (!dateString) return 0;
  const targetDate = new Date(dateString);
  const today = new Date();
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export default function Pickup({
  setDisableContinue,
  setFulfillmentObject,
  route,
}) {
  const [fulfillmentDate, setFulfillmentDate] = useState("");
  const [slotRef, setSlotRef] = useState(null);
  const [selectedDropLocation, setSelectedDropLocation] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [customPickup, setCustomPickup] = useState(false);
  const [customPickupNotes, setCustomPickupNotes] = useState("");
  const [alternatePickupPerson, setAlternatePickupPerson] = useState("");
  const [isPending, setIsPending] = useState(true);
  const { user: authUser, authIsReady } = useAuthContext();
  const { user } = useUser();
  const { previousOrderInfo, isLoading: isPreviousOrderLoading } =
    usePreviousOrder();
  const [taxRate, setTaxRate] = useState(null);

  const pickupTimeArray = handlePickupTimes(route ? route.pickupTimes : null);
  const dropLocationArray = route ? route.dropLocations : null;
  const orderMonth = route ? route.currentOrder : null;

  const locationOptions = pickupTimeArray
    ? Array.from(new Set(pickupTimeArray.map((p) => p.location)))
    : [];

  const dateOptions =
    pickupTimeArray && selectedDropLocation
      ? Array.from(
          new Set(
            pickupTimeArray
              .filter((p) => p.location === selectedDropLocation)
              .map((p) => p.date)
          )
        )
      : [];

  // Helper function to calculate days remaining for a specific date
  const getDaysRemainingForDate = (dateString) => {
    if (!selectedDropLocation || !pickupTimeArray || !dateString) {
      return 1;
    }

    // Find the first pickup time for this location and date
    const pickupTime = pickupTimeArray.find(
      (p) => p.location === selectedDropLocation && p.date === dateString
    );

    if (!pickupTime || !pickupTime.orderDeadline) return 1;

    // Calculate days remaining from the pickup time's orderDeadline
    return Math.ceil(
      (new Date(pickupTime.orderDeadline) - new Date()) / (1000 * 60 * 60 * 24)
    );
  };

  // Calculate days remaining until order deadline based on selected date
  const daysRemaining = getDaysRemainingForDate(selectedDate);

  useEffect(() => {
    // Don't overwrite fulfillmentObject when we have a previous order
    if (previousOrderInfo) {
      return;
    }

    if (fulfillmentDate.length > 0) {
      // Find the orderDeadline from the selected pickup time
      let orderDeadline = null;
      if (pickupTimeArray && selectedDropLocation && selectedDate) {
        const selectedPickupTime = pickupTimeArray.find(
          (p) => p.location === selectedDropLocation && p.date === selectedDate
        );
        orderDeadline = selectedPickupTime?.orderDeadline || null;
      }

      const selectedDropLocationObject =
        dropLocationArray?.find((d) => d.name === selectedDropLocation) || null;

      const fulfillmentObject = {
        type: "Pickup",
        location: selectedDropLocation,
        date: selectedDate,
        pickupAddress: selectedDropLocationObject?.address || null,
        fulfillmentDate,
        slotRef,
        taxRate,
        customPickup,
        customPickupNotes,
        orderMonth,
        additionalOrder: "false",
        alternatePickupPerson,
        orderDeadline,
      };

      setFulfillmentObject(fulfillmentObject);
    }
  }, [
    customPickup,
    customPickupNotes,
    dropLocationArray,
    setDisableContinue,
    fulfillmentDate,
    slotRef,
    taxRate,
    pickupTimeArray,
    selectedDropLocation,
    selectedDate,
    previousOrderInfo,
  ]);

  useEffect(() => {
    if (previousOrderInfo) {
      // Auto-populate form with previous order information
      // previousOrderInfo already includes orderDeadline from MongoDB
      // Use current orderMonth instead of the one from previous order
      setFulfillmentObject({
        ...previousOrderInfo,
        additionalOrder: true,
        orderMonth, // Override with current orderMonth
      });

      // populate local state so the selected fulfillment remains
      setFulfillmentDate(previousOrderInfo.fulfillmentDate || "");
      setSlotRef(previousOrderInfo.slotRef || null);
      setCustomPickup(!!previousOrderInfo.customPickup);
      if (previousOrderInfo.location)
        setSelectedDropLocation(previousOrderInfo.location);
      if (previousOrderInfo.date) setSelectedDate(previousOrderInfo.date);
      if (previousOrderInfo.taxRate) setTaxRate(previousOrderInfo.taxRate);

      setDisableContinue(false);
    }

    // Set isPending to false once we've checked for previous orders
    if (!isPreviousOrderLoading) {
      setIsPending(false);
    }
  }, [
    previousOrderInfo,
    isPreviousOrderLoading,
    setFulfillmentObject,
    setDisableContinue,
    orderMonth,
  ]);

  useEffect(() => {
    if (
      pickupTimeArray &&
      pickupTimeArray.length > 0 &&
      !selectedDropLocation
    ) {
      const uniqueLocations = Array.from(
        new Set(pickupTimeArray.map((p) => p.location))
      );

      // prefer the user's saved location when available
      const preferredFromUser =
        user && user.location && uniqueLocations.includes(user.location)
          ? user.location
          : null;

      const chosen = preferredFromUser || uniqueLocations[0] || "";
      setSelectedDropLocation(chosen);

      // also initialize selectedDate to the first available date for that chosen location
      if (chosen) {
        const datesForChosen = Array.from(
          new Set(
            pickupTimeArray
              .filter((p) => p.location === chosen)
              .map((p) => p.date)
          )
        );
        if (datesForChosen && datesForChosen.length > 0) {
          setSelectedDate(datesForChosen[0]);
        }
      }
    }
  }, [pickupTimeArray, selectedDropLocation, authUser]);

  useEffect(() => {
    if (selectedDropLocation && dateOptions && dateOptions.length > 0) {
      setSelectedDate((prev) => prev || dateOptions[0]);
    }
  }, [selectedDropLocation, dateOptions]);

  useEffect(() => {
    // keep previous fulfillment when this is an additional order
    if (previousOrderInfo) {
      return;
    }

    // clear any previously selected fulfillment if location or date changes
    setFulfillmentDate("");
    setSlotRef(null);
    setDisableContinue(true);
  }, [selectedDropLocation, selectedDate, previousOrderInfo]);

  // When a location and date are selected, default to the first available time
  useEffect(() => {
    if (
      selectedDropLocation &&
      selectedDate &&
      pickupTimeArray &&
      pickupTimeArray.length > 0 &&
      !fulfillmentDate
    ) {
      const first = pickupTimeArray.find(
        (p) => p.location === selectedDropLocation && p.date === selectedDate
      );
      if (first) {
        setFulfillmentDate(first.fulfillmentString);
        setSlotRef(first.slotRef);
        setCustomPickup(false);
        setDisableContinue(false);

        // set taxRate from dropLocationArray if available
        if (dropLocationArray) {
          const dropLocationObject = dropLocationArray.find(
            (d) => d.name === selectedDropLocation
          );
          if (dropLocationObject) setTaxRate(dropLocationObject.taxRate);
        }
      }
    }
  }, [
    selectedDropLocation,
    selectedDate,
    pickupTimeArray,
    fulfillmentDate,
    dropLocationArray,
  ]);

  const handleCustomPickup = () => {
    if (customPickup) {
      setCustomPickup(false);
      setDisableContinue(true);
    } else {
      const dropLocationObject =
        (dropLocationArray &&
          dropLocationArray.find((obj) => obj.name === selectedDropLocation)) ||
        (dropLocationArray && dropLocationArray[0]);
      setFulfillmentDate("Custom pickup");
      setSlotRef({ locationId: 0, pickupId: 0 });
      setCustomPickup(true);
      setDisableContinue(false);

      setTaxRate(dropLocationObject.taxRate);
    }
  };

  const handleLocation = (selection) => {
    const { fulfillmentString, location, slotRef } = selection;
    setFulfillmentDate(fulfillmentString);
    setSlotRef(slotRef);
    setCustomPickup(false);
    setDisableContinue(false);

    /* the find() method is called on the dropLocationArray, 
    and it takes a callback function as an argument. 
    The callback function checks if the "name" property of the
    object passed to it equals [locationName].
    If it does, then the find() method returns that object. */

    const dropLocationObject = dropLocationArray.find(
      (obj) => obj.name === location
    );

    setTaxRate(dropLocationObject.taxRate);
  };

  return (
    <div className="pickup">
      <h3>Schedule a pick-up time</h3>
      {!isPending && !previousOrderInfo && (
        <p>
          Please carefully note the pick-up times and locations. 🧐 They may be
          different from your last order!
        </p>
      )}
      {isPending && <Loading />}
      {!isPending && previousOrderInfo && (
        <>
          <p>Your scheduled pick-up time is:</p>
          <p>
            <b> {previousOrderInfo.fulfillmentDate}</b>
          </p>
          {previousOrderInfo.daysRemaining !== undefined && (
            <p>
              {previousOrderInfo.daysRemaining < 1
                ? "Today is the last day to order."
                : `You have ${previousOrderInfo.daysRemaining} ${
                    previousOrderInfo.daysRemaining === 1 ? "day" : "days"
                  } remaining to add to your order.`}
            </p>
          )}
          <p>
            Please continue to the next page to complete your current order.
          </p>
        </>
      )}
      {!isPending && !previousOrderInfo && (
        <>
          <form>
            <p>Please select a pick-up time and location:</p>
            {pickupTimeArray && pickupTimeArray.length > 0 && (
              <>
                <label htmlFor="locationSelect">Choose a location:</label>
                <select
                  id="locationSelect"
                  value={selectedDropLocation}
                  onChange={(e) => {
                    setSelectedDropLocation(e.target.value);
                    setSelectedDate("");
                  }}
                >
                  {locationOptions.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                {dropLocationArray &&
                  selectedDropLocation &&
                  (() => {
                    const loc = dropLocationArray.find(
                      (d) => d.name === selectedDropLocation && d.ordersOpen
                    );
                    if (!loc) return null;
                    return (
                      <div key={loc.name}>
                        <p>
                          <b>Pickup Address:</b> {loc.address}
                        </p>
                      </div>
                    );
                  })()}
                {selectedDropLocation && (
                  <>
                    <p>Choose a date:</p>
                    {dateOptions.map((d) => {
                      const daysForThisDate = getDaysRemainingForDate(d);
                      return (
                        <div key={d} className="date-option">
                          <input
                            type="radio"
                            id={`date-${d}`}
                            value={d}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            name="dateOption"
                            checked={d === selectedDate}
                          />
                          <label htmlFor={`date-${d}`}>
                            {d} |{" "}
                            {daysForThisDate < 1
                              ? "Today is the last day to order"
                              : `${daysForThisDate} ${
                                  daysForThisDate === 1 ? "day" : "days"
                                } remaining to add to order`}
                          </label>
                        </div>
                      );
                    })}
                    {dropLocationArray &&
                      (() => {
                        const selectedLocation = dropLocationArray.find(
                          (loc) => loc.name === selectedDropLocation
                        );

                        if (
                          !selectedLocation ||
                          !selectedLocation.futureDelivery
                        ) {
                          return null;
                        }

                        const formattedDate = formatDate(
                          selectedLocation.futureDelivery
                        );
                        const ordersOpenDate = formatDateMonthDay(
                          selectedLocation.futureDeliveryOpens
                        );

                        return (
                          <p
                            style={{
                              fontSize: "0.9em",
                              color: "#666",
                              marginTop: "10px",
                            }}
                          >
                            Future Pick-up: {formattedDate} | Orders open on{" "}
                            {ordersOpenDate}
                          </p>
                        );
                      })()}
                  </>
                )}

                {selectedDropLocation && selectedDate && <p>Choose a time:</p>}

                {selectedDropLocation &&
                  selectedDate &&
                  pickupTimeArray
                    .filter(
                      (p) =>
                        p.location === selectedDropLocation &&
                        p.date === selectedDate
                    )
                    .map((option) => {
                      const {
                        location,
                        time,
                        slotRef,
                        fulfillmentString,
                        date,
                      } = option;

                      return (
                        <div key={fulfillmentString} className="time-option">
                          <input
                            type="radio"
                            id={fulfillmentString}
                            value={fulfillmentString}
                            onChange={(e) =>
                              handleLocation({
                                fulfillmentString: e.target.value,
                                slotRef,
                                location,
                              })
                            }
                            name="option"
                            checked={fulfillmentString === fulfillmentDate}
                          />
                          <label htmlFor={fulfillmentString}>
                            {date} | {time}
                          </label>
                        </div>
                      );
                    })}
              </>
            )}
          </form>
          <div>
            <hr />
            <h3>Add Pick-up Person</h3>
            <p>
              <i>
                Plan to have a friend or family member pick up your order? Let
                us know here.
              </i>
            </p>
            <input
              className="pickupNotes"
              type="text"
              placeholder="Enter pickup person"
              onChange={(e) => setAlternatePickupPerson(e.target.value)}
            />
          </div>
          <div className="customPickup">
            <input
              type="checkbox"
              id="customPickup"
              name="customPickup"
              checked={customPickup}
              onChange={(e) => handleCustomPickup()}
            />
            <label htmlFor={"customPickup"}>
              I need to schedule a custom pick-up time in Parsons.
            </label>
          </div>
          {customPickup && (
            <div>
              <hr />
              <p>
                <i>
                  Not available for pick-up at these times? Let us know and we
                  can help you schedule another time:
                </i>
              </p>
              <input
                className="pickupNotes"
                type="text"
                placeholder="Enter Pickup Notes"
                onChange={(e) => setCustomPickupNotes(e.target.value)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

const handlePickupTimes = (pickupTimes) => {
  if (pickupTimes) {
    const pickupTimesArray = pickupTimes.flatMap((dropLocation) => {
      const {
        open,
        dropLocation: name,
        locationId,
        pickupId,
        time,
        route,
        date,
        fulfillmentString,
        orderDeadline,
      } = dropLocation;

      if (!open) {
        return [];
      }
      return {
        location: name,
        time,
        slotRef: { locationId, pickupId },
        route,
        date,
        fulfillmentString,
        orderDeadline,
      };
    });

    return pickupTimesArray;
  }
};
